/**
 * Household selectors — memoized via reselect.
 */

import { createSelector } from 'reselect';
import { User, Household } from '@/infra/types/domain';
import { HouseholdState } from './ducks';
import { AppDefaults } from '@/infra/wasm/AppDefaults';

// ─── Input Selectors ───────────────────────────────────────

type StateWithHousehold = { household: HouseholdState } | HouseholdState;

function getHouseholdState(state: StateWithHousehold): HouseholdState {
  return 'household' in state && 'users' in (state as any).household
    ? (state as { household: HouseholdState }).household
    : (state as HouseholdState);
}

// ─── Owner Colors ──────────────────────────────────────────

const OWNER_COLORS = [
  '#4A90D9', // blue
  '#7B68EE', // purple
  '#FF6B6B', // coral
  '#4ECDC4', // teal
];

/**
 * Pure lookup for the palette color assigned to a user based on their position
 * in the household list. Exposed so renderers outside Redux-subscribed
 * components (calendar event blocks, etc.) can style per-owner without
 * plumbing state through.
 */
export function getUserColor(users: User[], userId: string): string {
  const index = users.findIndex((u) => u.id === userId);
  return OWNER_COLORS[index >= 0 ? index % OWNER_COLORS.length : 0];
}

// ─── Derived Selectors ─────────────────────────────────────

export const selectHousehold = (state: StateWithHousehold): Household | null => {
  return getHouseholdState(state).household;
};

export const selectHouseholdUsers = createSelector(
  [(state: StateWithHousehold) => getHouseholdState(state).users],
  (users): User[] => users
);

export const selectCurrentUserId = (state: StateWithHousehold): string | null => {
  return getHouseholdState(state).currentUserId;
};

/**
 * Get a consistent color for a user based on their position in the household.
 */
export const selectUserColor = (state: StateWithHousehold, userId: string): string => {
  return getUserColor(getHouseholdState(state).users, userId);
};

export const selectCurrentUser = createSelector(
  [
    (state: StateWithHousehold) => getHouseholdState(state).users,
    (state: StateWithHousehold) => getHouseholdState(state).currentUserId,
  ],
  (users, currentUserId): User | undefined => {
    return users.find((u) => u.id === currentUserId);
  }
);

// selectDayStartHour returns the current user's day_start_hour with a
// fallback to the wasm-sourced AppDefaults when the user / preference
// isn't yet loaded. Single source of truth for the fallback so
// consumers don't drift; mirrors Go's `core.DayStartHourFallback`.
export const selectDayStartHour = (state: StateWithHousehold): number => {
  const u = selectCurrentUser(state) as (User & { day_start_hour?: number }) | undefined;
  return u?.day_start_hour ?? AppDefaults.dayStartHourFallback;
};

// selectDefaultHour returns the current user's default_hour with a
// fallback through AppDefaults. Mirrors Go's `core.DefaultHourFallback`.
export const selectDefaultHour = (state: StateWithHousehold): number => {
  const u = selectCurrentUser(state) as (User & { default_hour?: number }) | undefined;
  return u?.default_hour ?? AppDefaults.defaultHourFallback;
};

export const selectHouseholdLoading = (state: StateWithHousehold): boolean => {
  return getHouseholdState(state).loading;
};

export const selectHouseholdError = (state: StateWithHousehold): string | null => {
  return getHouseholdState(state).error;
};
